import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { ActiveLocationsListConfiguration } from '../../../../core/contract/user-configuration/active-locations-list-configuration.contract';
import { ColumnConfig } from '../../../../core/contract/user-configuration/column-config.contract';
import { LanguageService } from '../../../../core/services/language.service';
import { UserConfigurationService } from '../../../../core/services/user-configuration.service';
import { ColumnSort } from '../../../../osp-ui/components/data/table/contract/column-sort.contract';
import { TableColumn } from '../../../../osp-ui/components/data/table/contract/table-column.contract';
import { COLUMN_DEFAULT_WIDTH_PX } from '../../../../osp-ui/components/data/table/table.constant';
import { TableCellHtmlComponent } from '../../../../shared/components/table-cell/table-cell-html/table-cell-html.component';
import { TableCellLabelsComponent } from '../../../../shared/components/table-cell/table-cell-labels/table-cell-labels.component';
import { PAGEABLE_DATASOURCE_DEFAULT_PAGE_SIZE } from '../../../../shared/constants/pageable-datasource.const';
import { TimezoneFormatLocaleDate } from '../../../../shared/contract/timezone-format-local-date.contract';
import { LocationType } from '../../../../shared/enums/location-type.enum';
import { AddressService } from '../../../../shared/services/address.service';
import { DatesService } from '../../../../shared/services/dates.service';
import { ActiveLocationCategoryCellComponent } from '../components/active-locations-list/components/active-location-category-cell/active-location-category-cell.component';
import { ActiveLocationTypeCellComponent } from '../components/active-locations-list/components/active-location-type-cell/active-location-type-cell.component';
import { ActiveLocation } from '../contract/active-location.contract';
import { ActiveLocationColumnKeys } from '../enums/active-location-column-keys.enum';

@Injectable({ providedIn: 'root' })
export class ActiveLocationsListConfigService {
  private readonly languageService = inject(LanguageService);
  private readonly datesService = inject(DatesService);
  private readonly userConfigurationService = inject(UserConfigurationService);
  private readonly addressService = inject(AddressService);

  private readonly dateFormat: TimezoneFormatLocaleDate = {
    showTime: false,
  };

  readonly availableColumns: TableColumn<ActiveLocation>[] = [
    {
      key: ActiveLocationColumnKeys.INTERNAL_NUMBER,
      field: 'internalNumber',
      header: this.translate('general.intNumber.abbr'),
      readableName: this.translate('general.intNumber.abbr'),
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => (row.type === LocationType.PROJECT ? row.internalNumber ?? '' : ''),
    },
    {
      key: ActiveLocationColumnKeys.NAME,
      field: 'name',
      header: this.translate('general.name.s'),
      readableName: this.translate('general.name.s'),
      resizable: true,
      sortable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
    },
    {
      key: ActiveLocationColumnKeys.LOCATION_TYPE,
      field: 'type',
      header: this.translate('general.columnNames.type'),
      readableName: this.translate('general.columnNames.type'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      component: ActiveLocationTypeCellComponent,
    },
    {
      key: ActiveLocationColumnKeys.LOCATION_CATEGORY,
      field: 'category',
      header: this.translate('general.columnNamesValue.category'),
      readableName: this.translate('general.columnNamesValue.category'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      component: ActiveLocationCategoryCellComponent,
    },
    {
      key: ActiveLocationColumnKeys.COST_CENTER,
      field: 'costCenter',
      header: this.translate('general.costCenter'),
      readableName: this.translate('general.costCenter'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
    },
    {
      key: ActiveLocationColumnKeys.ADDRESS,
      field: 'address',
      header: this.translate('general.address.address'),
      readableName: this.translate('general.address.address'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => this.addressService.format(row.address),
    },
    {
      key: ActiveLocationColumnKeys.ORGANISATION,
      field: 'organisationName',
      header: this.translate('general.columnNamesValue.organisation'),
      readableName: this.translate('general.columnNamesValue.organisation'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
    },
    {
      key: ActiveLocationColumnKeys.SCAN_CODE,
      field: 'scanCode',
      header: this.translate('general.scanCode'),
      readableName: this.translate('general.scanCode'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
    },
    {
      key: ActiveLocationColumnKeys.START_DATE,
      field: 'start',
      header: this.translate('modules.locations.active.list.columns.startDate'),
      readableName: this.translate('modules.locations.active.list.columns.startDate'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => this.formatProjectDate(row, row.start),
    },
    {
      key: ActiveLocationColumnKeys.END_DATE,
      field: 'end',
      header: this.translate('modules.locations.active.list.columns.endDate'),
      readableName: this.translate('modules.locations.active.list.columns.endDate'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => this.formatProjectDate(row, row.end),
    },
    {
      key: ActiveLocationColumnKeys.DESCRIPTION,
      field: 'description',
      header: this.translate('general.description'),
      readableName: this.translate('general.description'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      component: TableCellHtmlComponent,
      cbValue: (row) => row.description ?? '',
    },
    {
      key: ActiveLocationColumnKeys.POSITION,
      field: 'position',
      header: this.translate('modules.locations.active.list.columns.position'),
      readableName: this.translate('modules.locations.active.list.columns.position'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => {
        if (!row.position) {
          return '';
        }

        return `${row.position.lat} / ${row.position.lon}`;
      },
    },
    {
      key: ActiveLocationColumnKeys.LABELS,
      field: 'labels',
      header: this.translate('general.label.s'),
      readableName: this.translate('general.label.s'),
      sortable: true,
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      component: TableCellLabelsComponent,
    },
  ];

  private readonly defaultColumns = new Set<string>([
    ActiveLocationColumnKeys.INTERNAL_NUMBER,
    ActiveLocationColumnKeys.NAME,
    ActiveLocationColumnKeys.LOCATION_TYPE,
    ActiveLocationColumnKeys.LOCATION_CATEGORY,
    ActiveLocationColumnKeys.COST_CENTER,
    ActiveLocationColumnKeys.ADDRESS,
    ActiveLocationColumnKeys.ORGANISATION,
  ]);

  private readonly _selectedColumnKeys$ = new BehaviorSubject<string[]>(Array.from(this.defaultColumns));
  private readonly _pageSize$ = new BehaviorSubject<number>(PAGEABLE_DATASOURCE_DEFAULT_PAGE_SIZE);
  private readonly _sort$ = new BehaviorSubject<ColumnSort | null>(null);

  readonly selectedColumns$ = this._selectedColumnKeys$.pipe(
    map((keys) => keys.map((key) => this.getColumn(key))),
    map((columns) => columns.filter((column): column is TableColumn<ActiveLocation> => !!column)),
  );

  get pageSize(): number {
    return this._pageSize$.getValue();
  }

  get sort(): ColumnSort | null {
    return this._sort$.getValue();
  }

  constructor() {
    this.initConfigs();
  }

  getColumn(columnKey: string): TableColumn<ActiveLocation> | null {
    return this.availableColumns.find(({ key }) => key === columnKey) || null;
  }

  getSelectedColumnsKeys(): string[] {
    return this._selectedColumnKeys$.getValue();
  }

  saveSelectedColumns(columnKeys: string[]): void {
    this._selectedColumnKeys$.next(columnKeys);
    this.saveListConfigs();
  }

  savePageSize(size: number): void {
    if (size > 0 && size !== this.pageSize) {
      this._pageSize$.next(size);
      this.saveListConfigs();
    }
  }

  saveSort(sort: ColumnSort | null): void {
    if (this.sort?.field !== sort?.field || this.sort?.direction !== sort?.direction) {
      this._sort$.next(sort);
      this.saveListConfigs();
    }
  }

  saveColumnConfigs(config: ColumnConfig): void {
    this.mergeColumns([config]);
    this._selectedColumnKeys$.next(this._selectedColumnKeys$.getValue());
    this.saveListConfigs();
  }

  isDefaultColumn(columnKey: string): boolean {
    return this.defaultColumns.has(columnKey);
  }

  private initConfigs(): void {
    const config = this.userConfigurationService.getActiveLocationsListConfiguration();

    this._pageSize$.next(config?.pageSize || PAGEABLE_DATASOURCE_DEFAULT_PAGE_SIZE);
    this._selectedColumnKeys$.next(config?.columns?.length ? config.columns : Array.from(this.defaultColumns));
    this._sort$.next(config?.sort ?? null);
    this.mergeColumns(config?.columnConfigs);
  }

  private mergeColumns(columnConfigs: ColumnConfig[]): void {
    (columnConfigs ?? []).forEach(({ name, width }) => {
      const foundColumn = this.getColumn(name);
      if (foundColumn && !foundColumn.hardcoded) {
        foundColumn.widthPx = width;
      }
    });
  }

  private saveListConfigs(): void {
    const configs: ActiveLocationsListConfiguration = {
      pageSize: this._pageSize$.getValue(),
      columns: this._selectedColumnKeys$.getValue(),
      columnConfigs: this.availableColumns.map(({ key: name, widthPx: width }) => ({ name, width })),
      sort: this._sort$.getValue(),
    };
    this.userConfigurationService.saveActiveLocationsListConfiguration(configs).subscribe();
  }

  private formatProjectDate(row: ActiveLocation, date?: Date): string {
    if (row.type !== LocationType.PROJECT || !date) {
      return '';
    }

    return this.datesService.dateFormatLocale(date, this.dateFormat) ?? '';
  }

  private translate(key: string, interpolateParams?: object): string {
    return this.languageService.getInstant(key, interpolateParams);
  }
}
